import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getConsumerList } from "../../../services/panel/panelServices";

const PAGE_SIZE = 10;

const formatAmount = (amt) =>
  amt ? `₹${Number(amt).toLocaleString("en-IN")}` : "—";

export default function SavedCA() {
  const navigate = useNavigate();
  const [isConsumerData, setConsumerData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConsumerList();
  }, [pageNumber, searchText]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPageNumber(1);
      setSearchText(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchConsumerList = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber,
        pageSize: PAGE_SIZE,
        categoryId: 0,
        searchText,
      };
      const res = await getConsumerList(params);

      if (res?.statuscode === 1) {
        setConsumerData(res?.records || []);
        setTotalRecords(res?.totalRecords || 0);
      } else {
        setConsumerData([]);
        setTotalRecords(0);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch consumer list",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  return (
    <>
      <div className="screen active" id="screen-saved-ca">
        <div className="page-head">
          <div>
            <h1>Saved consumer accounts</h1>
            <div className="ph-sub">
              {totalRecords} accounts registered under your corporate
            </div>
          </div>
        </div>
        <div className="table-toolbar">
          <div className="search-box">
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx={11}
                cy={11}
                r={7}
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M21 21l-4-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            <input
              placeholder="Search consumer accounts"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
        <div className="card">
          <div className="table-wrap">
            <table className="data-table" id="saved-ca-table">
              <tbody>
                <tr>
                  <th>Biller</th>
                  <th>Account</th>
                  <th className="tright">Amount</th>
                  <th className="tright">Due</th>
                  <th className="tright">Status</th>
                  <th className="tright">Action</th>
                </tr>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="empty-note">
                      Loading accounts...
                    </td>
                  </tr>
                ) : isConsumerData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-note">
                      No consumer accounts found
                    </td>
                  </tr>
                ) : (
                  isConsumerData.map((acc) => (
                    <tr key={acc.id}>
                      <td>
                        {acc.billerName}
                        <div
                          style={{ fontSize: 10, color: "var(--text-faint)" }}
                        >
                          {acc.categoryName} · {acc.uid}
                        </div>
                      </td>
                      <td>
                        {acc.consumerName || acc.nickname}
                        <div
                          style={{ fontSize: 10, color: "var(--text-faint)" }}
                          className="mono"
                        >
                          {acc.accountNumber}
                        </div>
                      </td>
                      <td className="mono tright">
                        {formatAmount(acc.amount)}
                      </td>
                      <td className="tright">{acc.due || "—"}</td>
                      <td className="tright">
                        {acc.isActive ? (
                          <span className="pill pill-success">Active</span>
                        ) : (
                          <span className="pill pill-neutral">Inactive</span>
                        )}
                      </td>
                      <td className="tright">
                        {acc.isActive ? (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() =>
                              navigate("/bill-review", {
                                state: { account: acc },
                              })
                            }
                          >
                            Pay now
                          </button>
                        ) : (
                          <span
                            style={{ color: "var(--text-faint)", fontSize: 11 }}
                          >
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalRecords > PAGE_SIZE && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 14,
                fontSize: "11.5px",
                color: "var(--text-faint)",
              }}
            >
              <span>
                Page {pageNumber} of {totalPages} · {totalRecords} accounts
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={pageNumber <= 1 || loading}
                  onClick={() => setPageNumber((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={pageNumber >= totalPages || loading}
                  onClick={() => setPageNumber((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
